import { ChevronDown } from '@styled-icons/bootstrap/ChevronDown';
import { ChevronUp } from '@styled-icons/bootstrap/ChevronUp';
import { Fullscreen } from '@styled-icons/bootstrap/Fullscreen';
import { ButtonPrimary, ImpactClaimButton, Media, MediaDetailViewModal, SlideIndicator, } from 'components/Shared';
import FlexBox from 'components/Shared/Container/FlexBox';
import ExpandableContent from 'components/Shared/ExpandableContent';
import Icon from 'components/Shared/Icon/Icon';
import ListItem from 'components/Shared/VerifiedPayments/ListItem';
import PreviewedList from 'components/Shared/VerifiedPayments/PreviewedList';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Carousel } from 'react-responsive-carousel';
import useMeasure from 'react-use-measure';
import styled from 'styled-components';
import { convertHexToRGBA } from 'utils/color';
import { getPrimaryImage } from 'utils/supplyChain';
import { getTextFromHTMLString } from 'utils/text';
import Marker from '../Map/Marker/Marker';
import { SiteType } from 'types/site.types';
import { ArrowRight } from '@styled-icons/bootstrap/ArrowRight';
import PaymentsMarker from 'components/Shared/VerifiedPayments/Marker';
const PADDING_SLIDE_INDICATOR = 14;
const MEDIA_HEIGHT = 166;
const Container = styled.div `
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius};
  z-index: 4;
  max-width: 400px;
  background: #fff;

  .carousel .slider {
    overflow: visible !important;
  }
`;
const ContentContainer = styled.div `
  position: relative;
  padding: 14px 14px 16px;
  font-size: 14px !important;
  font-weight: 400 !important;
  line-height: 17px !important;
`;
const StyledMarker = styled(Marker) `
  transform: scale(0.7);
`;
const MediaListContainer = styled.div `
  position: relative;
  border-radius: ${({ hasBorderBottomRadius }) => hasBorderBottomRadius ? '8px' : '8px 8px 0 0'};
  overflow: hidden !important;

  .control-dots {
    display: flex;
    position: inherit !important;
    padding: 0 16px !important;
    margin: 0 !important;
    max-height: 0;
    transition: all 0.2s ease-out;
    opacity: 0;

    ${({ expanded }) => expanded &&
    `
      margin-top: 16px !important;
      opacity: 1;
      max-height: 1000px;
      transition: all .3s ease-in;
  `}
  }
`;
const MediaListItem = styled(Media) `
  width: 100%;
  height: ${MEDIA_HEIGHT}px;
  overflow: hidden;
  background: transparent;
  border-radius: ${({ hasBorderBottomRadius }) => hasBorderBottomRadius ? '8px' : '8px 8px 0 0'};

  :after {
    content: '';
    background: transparent;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }

  iframe {
    width: 100% !important;
    margin: 0 !important;
  }

  img {
    object-fit: cover;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    overflow: hidden !important;
  }
`;
const Header = styled.div `
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 4px 36px 0 8px;
`;
const Title = styled.div `
  font-size: 16px !important;
  font-weight: 800 !important;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;
const HeaderTextContainer = styled.div `
  color: ${({ hasMedia, theme }) => hasMedia ? '#fff' : theme.palette.grey['A800']};
  width: 100%;
  overflow: hidden !important;
`;
const Location = styled.div `
  line-height: 1;
  font-weight: 300;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;
const OverlayContainer = styled.div `
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  height: ${MEDIA_HEIGHT}px;
  border-top-left-radius: ${({ theme }) => theme.borderRadius};
  border-top-right-radius: ${({ theme }) => theme.borderRadius};
  pointer-events: none;
  z-index: 1;
  // seems to fix the problem that the node overlay is not displayed on small screens in safari
  -webkit-transform: translate3d(0, 0, 0);
`;
const FullscreenButton = styled(ButtonPrimary) `
  position: absolute;
  padding: 6px !important;
  bottom: 14px;
  right: 14px;
  background: #000 !important;
  opacity: 0.5;
  transition: all 0.3s ease-out;

  ${({ hasExtraMargin }) => hasExtraMargin &&
    `
      bottom: 22px;
      transition: all .3s ease-in;
  `}
`;
const ExpandMinimizeIconContainer = styled.div `
  position: absolute;
  top: 4px;
  right: 4px;
  color: #fff;
  padding: 8px;
  pointer-events: fill;
`;
// eslint-disable-next-line
const ClaimsButtonContainer = styled.div `
  position: absolute;
  bottom: 14px;
  left: 14px;
  pointer-events: fill;
  transition: all 0.3s ease-out;

  ${({ hasExtraMargin }) => hasExtraMargin &&
    `
      bottom: 22px;
      transition: all .3s ease-in;
  `}
`;
const CarouselIndicator = styled.div `
  width: 20%;
  margin-right: 8px;
  position: relative;
  height: 30px;
  cursor: pointer;
  z-index: 3;

  .carousel-indicator-media {
    z-index: 3;
    width: 100%;
    margin: 0 !important;
    height: 100%;
    border-radius: ${({ theme }) => theme.borderRadius};
  }

  img {
    height: 30px;
    object-fit: cover;
    ${({ active, theme }) => active &&
    `
      border: 2px solid ${theme.palette.grey[400]};
    `}
  }
`;
const IndicatorOverlay = styled.div `
  position: absolute;
  border-radius: ${({ theme }) => theme.borderRadius};
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 4;
  background: linear-gradient(
    125.26deg,
    rgba(0, 0, 0, 0.4) 34.32%,
    rgba(0, 0, 0, 0.04) 77.01%
  );
`;
const StyledCarousel = styled(Carousel) `
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  overflow: hidden !important;

  .carousel .slide {
    background: transparent;
  }
`;
const SlideIndicatorContainer = styled.div `
  position: absolute;
  bottom: 10px;
  display: flex;
  padding: ${({ padding }) => `0 ${padding}`}px;
  opacity: 1;
  max-height: 100%;
  transition: all 0.3s ease-out;
  transform: translateY(0);
  z-index: 1;

  ${({ expanded }) => expanded &&
    `
      transform: translateY(15px);
      opacity: 0;
      max-height: 0;
      transition: all .3s ease-in;
  `}
`;
const ClickableContainer = styled.div `
  cursor: pointer;

  .image-gradient-overlay {
    border-radius: ${({ hasBorderRadius }) => hasBorderRadius ? '8px' : 'unset'};
  }
`;
const LocationWithoutImage = styled.div `
  height: ${MEDIA_HEIGHT}px;
  background: ${({ theme }) => convertHexToRGBA(theme.palette.primary.main, 0.16)};
`;
const StyledChevronUp = styled(ChevronUp) `
  color: ${({ isDark, theme }) => isDark ? theme.palette.grey['A500'] : theme.palette.grey['A100']};
`;
const StyledChevronDown = styled(ChevronDown) `
  color: ${({ isDark, theme }) => isDark ? theme.palette.grey['A500'] : theme.palette.grey['A100']};
`;
const OutputComponent = styled.div `
  display: inline-flex;
  padding: 4px 8px;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadiusButton};
  background: ${convertHexToRGBA('#ffffff', 0.8)};
  color: ${({ theme }) => theme.palette.grey.A800};
  box-shadow: ${({ theme }) => theme.shadows[0]};
  font-weight: 700;
  height: 28px;
  font-size: 14px;
  margin-left: 16px;
  margin-top: 4px;
`;
const DetailsContainer = styled(FlexBox) `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  padding: 12px;
  padding: 12px;
  border-radius: 6px;
  background: ${({ theme }) => theme.palette.grey[10]};
`;
const FarmerDetails = styled(FlexBox) `
  display: flex;
  width: 100%;
  align-items: flex-start;
  > div:first-of-type {
    flex: 30%;
  }
  > div {
    flex: 70%;
  }
`;
const DetailsHeader = styled.h6 `
  color: ${({ theme }) => theme.palette.grey.A500};
  font-family: Lato;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  text-transform: uppercase;
`;
const DetailsItem = styled.span `
  color: ${({ theme }) => theme.palette.grey.A800};
  font-family: Lato;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;
const ClusterTag = styled(FlexBox) `
  white-space: nowrap;
  overflow: hidden;
  font-family: Lato;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  text-overflow: ellipsis;
`;
const Activity = ({ activity, onImpactClaimsClick, onActivitySitesClick, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const { t } = useTranslation();
    const [mediaModalOpen, setMediaModalOpen] = useState(false);
    const [selectedMediaItem, setSelectedMediaItem] = useState(0);
    const [contentExpanded, setContentExpanded] = useState(false);
    const nodeHasMedia = !!((_a = activity === null || activity === void 0 ? void 0 : activity.mediaList) === null || _a === void 0 ? void 0 : _a.length);
    // to check if a description exists we have to strip html from the string (<p><br></p> could be a empty description as well)
    const descriptionString = getTextFromHTMLString(activity === null || activity === void 0 ? void 0 : activity.description);
    const nodeHasDescription = !!(descriptionString === null || descriptionString === void 0 ? void 0 : descriptionString.length); // TODO: or has farm details
    const nodeHasImpactClaims = !!((_b = activity === null || activity === void 0 ? void 0 : activity.impactClaims) === null || _b === void 0 ? void 0 : _b.edges.length);
    const carouselRef = useRef();
    const locationName = activity === null || activity === void 0 ? void 0 : activity.site.locationName;
    const site = activity === null || activity === void 0 ? void 0 : activity.site;
    // Make sure all sites in the cluster are farms
    const hasSiteCluster = (site === null || site === void 0 ? void 0 : site.siteType) === SiteType.CLUSTER && (site === null || site === void 0 ? void 0 : site.sites.count) > 1;
    const hasFarmCluster = hasSiteCluster &&
        (site === null || site === void 0 ? void 0 : site.sites.edges.every((edge) => edge.node.siteType === SiteType.FARM));
    const siteCluster = hasSiteCluster ? site : undefined;
    const hasSubActivities = activity.subActivities.length > 0;
    const hasAdditionalCrop = site.siteType === SiteType.FARM &&
        (site === null || site === void 0 ? void 0 : site.additionalRawMaterials) &&
        (site === null || site === void 0 ? void 0 : site.additionalRawMaterials.length) > 0;
    const [measureRef, { width }] = useMeasure();
    useEffect(() => {
        // @ts-ignore
        if (carouselRef === null || carouselRef === void 0 ? void 0 : carouselRef.current) {
            setSelectedMediaItem(0);
        }
    }, [activity, carouselRef.current]);
    const handleClickMediaItem = (index) => {
        setSelectedMediaItem(index);
        setMediaModalOpen(true);
    };
    // eslint-disable-next-line
    const handleClickClaims = (event) => {
        event.stopPropagation();
        onImpactClaimsClick();
    };
    const handleSeeAllFarms = (event) => {
        event.stopPropagation();
        event.preventDefault();
        onActivitySitesClick();
    };
    const handleSiteClick = (event) => {
        event.stopPropagation();
        event.preventDefault();
        if ((site === null || site === void 0 ? void 0 : site.siteType) === SiteType.CLUSTER) {
            onActivitySitesClick(site.sites.edges[0].node.id);
        }
        else {
            onActivitySitesClick(site.id);
        }
    };
    if (!activity)
        return null;
    return (React.createElement(Container, { ref: (ref) => {
            measureRef(ref);
        } },
        React.createElement(MediaListContainer, { expanded: contentExpanded && nodeHasDescription, hasBorderBottomRadius: !nodeHasDescription },
            nodeHasMedia ? (React.createElement(StyledCarousel
            // @ts-ignore
            , { 
                // @ts-ignore
                ref: (ref) => {
                    // @ts-ignore
                    carouselRef.current = ref;
                }, autoPlay: false, showArrows: false, showThumbs: false, showStatus: false, emulateTouch: true, swipeable: true, selectedItem: selectedMediaItem, onChange: (currentItem) => setSelectedMediaItem(currentItem), renderIndicator: (clickHandler, isSelected, index) => (React.createElement(CarouselIndicator, { onClick: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        clickHandler(e);
                    }, active: isSelected },
                    React.createElement(Media, { media: activity.mediaList[index], className: "carousel-indicator-media" }),
                    React.createElement(IndicatorOverlay, null))) }, activity.mediaList.map((media, index) => {
                return (React.createElement(ClickableContainer
                // eslint-disable-next-line react/no-array-index-key
                , { 
                    // eslint-disable-next-line react/no-array-index-key
                    key: index, hasBorderRadius: !nodeHasDescription, onClick: (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleClickMediaItem(index);
                    } },
                    React.createElement(MediaListItem, { displaySkeleton: true, gradientOverlay: true, media: media, hasBorderBottomRadius: !nodeHasDescription })));
            }))) : (React.createElement(LocationWithoutImage, null)),
            React.createElement(OverlayContainer, null,
                React.createElement(Header, null,
                    !!activity && (React.createElement(StyledMarker, { image: getPrimaryImage(activity.mediaList), renderedOn: "NODE", active: true })),
                    React.createElement(HeaderTextContainer, { hasMedia: nodeHasMedia },
                        React.createElement(FlexBox, { gap: 1, pt: 1 },
                            React.createElement(Title, null, activity.title),
                            ((_c = activity.site) === null || _c === void 0 ? void 0 : _c.siteType) === SiteType.CLUSTER && (React.createElement(ClusterTag, { gap: 0.5 },
                                React.createElement(Icon, { name: "farm", size: "large", color: nodeHasMedia ? 'white' : 'almost_black' }),
                                activity.site.sites.count,
                                ' ',
                                hasFarmCluster ? t('farms') : t('sites')))),
                        React.createElement(Location, null, locationName))),
                activity.component && (React.createElement(OutputComponent, null, activity.component.title)),
                nodeHasMedia && (React.createElement(FullscreenButton, { hasExtraMargin: activity.mediaList.length > 1 && !contentExpanded },
                    React.createElement(Fullscreen, { size: 20, color: "#fff" }))),
                nodeHasDescription && (React.createElement(ExpandMinimizeIconContainer, { onClick: (e) => {
                        e.stopPropagation();
                        setContentExpanded(!contentExpanded);
                    } }, contentExpanded ? (React.createElement(StyledChevronUp, { isDark: !nodeHasMedia, size: 20 })) : (React.createElement(StyledChevronDown, { isDark: !nodeHasMedia, size: 20 })))),
                nodeHasImpactClaims && (React.createElement(ClaimsButtonContainer, { hasExtraMargin: activity.mediaList.length > 1 && !contentExpanded },
                    React.createElement(ImpactClaimButton, { impactClaims: activity.impactClaims, handleClick: (e) => handleClickClaims(e), variant: nodeHasMedia ? 'dark' : 'light' }))),
                activity.mediaList.length > 1 ? (React.createElement(SlideIndicatorContainer, { padding: PADDING_SLIDE_INDICATOR, expanded: contentExpanded }, activity.mediaList.map((item, index) => (React.createElement(SlideIndicator
                // eslint-disable-next-line react/no-array-index-key
                , { 
                    // eslint-disable-next-line react/no-array-index-key
                    key: index, active: selectedMediaItem === index, width: (width - PADDING_SLIDE_INDICATOR * 2) /
                        activity.mediaList.length }))))) : null)),
        React.createElement(ClickableContainer, { onClick: () => setContentExpanded(!contentExpanded) },
            React.createElement(ContentContainer, null,
                React.createElement(ExpandableContent, { expanded: contentExpanded, onExpand: () => setContentExpanded(!contentExpanded), height: 55, text: activity.description },
                    contentExpanded && hasSiteCluster && (React.createElement(FlexBox, { gap: 2, mt: 2, flexDirection: "column", alignItems: "flex-start" },
                        (hasSubActivities ||
                            isNaN(siteCluster.sitesSummary.workersAmount) ||
                            siteCluster.sitesSummary.additionalRawMaterialTitles) && (React.createElement(DetailsContainer, { mb: 2 },
                            hasSubActivities && (React.createElement(FlexBox, { flexDirection: "column", alignItems: "flex-start" },
                                React.createElement(DetailsHeader, null, t('activities')),
                                React.createElement(DetailsItem, null, activity.subActivities
                                    .map(({ title }) => title)
                                    .join(', ')))),
                            hasFarmCluster &&
                                (isNaN(siteCluster.sitesSummary.workersAmount) ||
                                    siteCluster.sitesSummary
                                        .additionalRawMaterialTitles) && (React.createElement(FarmerDetails, null,
                                React.createElement(FlexBox, { flexDirection: "column", alignItems: "flex-start" },
                                    React.createElement(DetailsHeader, null, t('farmers')),
                                    React.createElement(DetailsItem, null, siteCluster.sitesSummary.workersAmount)),
                                ((_d = siteCluster.sitesSummary) === null || _d === void 0 ? void 0 : _d.additionalRawMaterialTitles) && (React.createElement(FlexBox, { flexDirection: "column", alignItems: "flex-start" },
                                    React.createElement(DetailsHeader, null, t('other-cultivated-crops')),
                                    React.createElement(DetailsItem, null, siteCluster.sitesSummary.additionalRawMaterialTitles.join(', ')))))))),
                        React.createElement(PreviewedList, { title: hasFarmCluster ? t('farms') : t('sites'), iconName: "farm", onSeeAllClick: handleSeeAllFarms },
                            React.createElement(ListItem, { preview: true, title: siteCluster.sites.edges[0].node.title, onClick: handleSiteClick, hideSubtitle: !hasFarmCluster ||
                                    !((_e = siteCluster.sites.edges[0].node) === null || _e === void 0 ? void 0 : _e.numberOfFarmers), subtitle: isNaN(siteCluster.sites.edges[0].node.siteType ===
                                    SiteType.FARM &&
                                    ((_f = siteCluster.sites.edges[0].node) === null || _f === void 0 ? void 0 : _f.numberOfFarmers))
                                    ? undefined
                                    : `${siteCluster.sites.edges[0].node.numberOfFarmers} ${t('farmers')}`, Icon: PaymentsMarker })))),
                    !siteCluster && (React.createElement(FlexBox, { gap: 2, mt: 2, flexDirection: "column", alignItems: "flex-start" },
                        (hasAdditionalCrop || hasSubActivities) && (React.createElement(DetailsContainer, null,
                            hasSubActivities && (React.createElement(FlexBox, { flexDirection: "column", alignItems: "flex-start" },
                                React.createElement(DetailsHeader, null, t('activities')),
                                React.createElement(DetailsItem, null, activity.subActivities
                                    .map(({ title }) => title)
                                    .join(', ')))),
                            hasAdditionalCrop && (React.createElement(FlexBox, { flexDirection: "column", alignItems: "flex-start" },
                                React.createElement(DetailsHeader, null, t('other-cultivated-crops')),
                                React.createElement(DetailsItem, null, site.additionalRawMaterials
                                    .map(({ title }) => title)
                                    .join(', ')))))),
                        site.siteType === SiteType.FARM &&
                            (getTextFromHTMLString(site.description).length ||
                                site.sizeHa ||
                                site.numberOfFarmers ||
                                ((_g = site.workersSummary) === null || _g === void 0 ? void 0 : _g.trainings) ||
                                ((_h = site.workersSummary) === null || _h === void 0 ? void 0 : _h.numberOfChildren) ||
                                ((_j = site.workersSummary) === null || _j === void 0 ? void 0 : _j.womanPercentage) !== undefined) ? (React.createElement(ListItem, { title: site.title, navigationIcon: React.createElement(ArrowRight, { size: 24 }), onClick: handleSiteClick, hideSubtitle: !(site === null || site === void 0 ? void 0 : site.numberOfFarmers), subtitle: isNaN(site.numberOfFarmers)
                                ? undefined
                                : `${site.numberOfFarmers} ${t('farmers')}`, Icon: PaymentsMarker })) : getTextFromHTMLString(site.description).length ? (React.createElement(ListItem, { title: site.title, navigationIcon: React.createElement(ArrowRight, { size: 24 }), onClick: handleSiteClick, hideSubtitle: true, Icon: PaymentsMarker })) : null))))),
        mediaModalOpen && (React.createElement(MediaDetailViewModal, { carouselItems: activity.mediaList, onClose: () => setMediaModalOpen(false), indexMediaList: selectedMediaItem }))));
};
export default Activity;
